import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "hidden", label: "ເຊື່ອງ" },
  { value: "removed", label: "ລຶບແລ້ວ" },
];

type ListingsSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: FilterConditionDTO[];
  q?: string;
  recentDays?: number;
};

export function ListingsFilter() {
  const nav = useNavigate({ from: "/app/moderation" });
  const search = useSearch({ from: "/app/moderation" }) as ListingsSearch;

  const filters = search.filters ?? [];
  const statusFilter = findCondition(filters, "status");
  const [searchValue, setSearchValue] = useState<string>(search.q ?? "");
  const statusValue = (statusFilter?.value as string) ?? "all";
  const recentOnly = search.recentDays === 7;

  useEffect(() => {
    setSearchValue(search.q ?? "");
  }, [search.q]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        offset: 0,
        q: val.trim() || undefined,
      },
    });
  }, 400);

  const applyStatus = (value: string) => {
    let next = removeConditions(filters, "status");
    if (value !== "all") {
      next = upsertCondition(next ?? [], {
        field: "status",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  };

  const hasFilter =
    searchValue.trim() || statusValue !== "all" || recentOnly;

  const toggleRecent = () => {
    nav({
      search: {
        ...search,
        offset: 0,
        recentDays: recentOnly ? undefined : 7,
      },
    });
  };

  return (
    <div className="mb-4 flex items-center justify-between gap-4 px-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Input
          placeholder="ຄົ້ນຫາຕາມຊື່ສິນຄ້າ ຫຼື SKU..."
          defaultValue={searchValue}
          onChange={(e) => {
            const v = e.target.value;
            debounced(v);
          }}
          className="h-8 sm:max-w-2xs"
        />
        <Select value={statusValue} onValueChange={applyStatus}>
          <SelectTrigger className="h-8 w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={recentOnly ? "default" : "outline"}
          size="sm"
          className="h-8"
          onClick={toggleRecent}
        >
          Publish ใหม่ (7 วัน)
        </Button>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: undefined,
                  q: undefined,
                  recentDays: undefined,
                },
              })
            }
          >
            ລ້າງຕົວກອງ
          </Button>
        )}
      </div>
    </div>
  );
}
